import * as React from 'react';

/**
 * StatCard represents a single stat card with value and label.
 * Uses semantic grouping (figure element) so screen readers
 * announce value and label together.
 *
 * Desktop: 137px × 132px, rounded-md (8px)
 * Mobile: 102.75px × 99px, rounded-md (8px)
 */
export interface StatCardProps {
  /** The statistic value to display (e.g., "31", "100M+") */
  value: string;
  /** The label describing the statistic (e.g., "Countries", "Members") */
  label: string;
  /** Text size for value: small (32px), medium (44px), large (54px). Defaults to large. */
  size?: 'small' | 'medium' | 'large';
  /** Variant for responsive sizing. Desktop is larger, mobile is smaller. */
  variant?: 'desktop' | 'mobile';
}

export const StatCard: React.FC<StatCardProps> = ({
  value,
  label,
  size = 'large',
  variant = 'desktop',
}) => {
  const isMobile = variant === 'mobile';

  // Mobile: 102.75px × 99px, rounded-md (8px), smaller text
  // Desktop: 137px × 132px, rounded-md (8px), larger text
  const cardClasses = isMobile
    ? 'w-[102.75px] h-[99px] rounded-md'
    : 'w-[137px] h-[132px] rounded-md';

  const shadowStyle = isMobile
    ? '0 3px 48px rgba(13, 79, 160, 0.16)'
    : '0 4px 64px rgba(13, 79, 160, 0.16)';

  // Value text sizes per Figma design:
  // Desktop: small=32px, medium=44px, large=54px
  // Mobile: small=24px, medium=33px, large=40.5px
  const desktopSizeMap = { small: 'text-[32px]', medium: 'text-[44px]', large: 'text-[54px]' };
  const mobileSizeMap = { small: 'text-[24px]', medium: 'text-[33px]', large: 'text-[40.5px]' };
  const valueTextClass = isMobile ? mobileSizeMap[size] : desktopSizeMap[size];

  // Label text sizes: Desktop 18px, Mobile 13.5px
  const labelTextClass = isMobile ? 'text-[13.5px]' : 'text-[18px]';

  // Gap between value and label: Desktop 4px (gap-1), Mobile 3px
  const gapClass = isMobile ? 'gap-[3px]' : 'gap-1';

  return (
    <figure
      className={`relative z-10 bg-neutral-white ${cardClasses} flex items-center justify-center text-center shrink-0 overflow-hidden p-3`}
      style={{ boxShadow: shadowStyle }}
    >
      <div className={`flex flex-col items-center ${gapClass}`}>
        <span
          className={`block font-primary font-extrabold text-brand-mid leading-none ${valueTextClass}`}
        >
          {value}
        </span>
        <figcaption
          className={`${labelTextClass} font-primary font-semibold text-brand-deep leading-none`}
        >
          {label}
        </figcaption>
      </div>
    </figure>
  );
};
